import React from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { makeStyles } from '@material-ui/core/styles'
import ShieldIcon from 'components/material-symbol/icons/Shield'
import { toBoolean } from 'lib/utils/boolean'

// Tokens shared with the redesigned private booking cards (soft panel grey,
// card border) plus the green callout from the booking summary mock.
const SOFT_PANEL = '#F5F6F8'
const CARD_BORDER = '#E6E8EC'
const FREE_BACKGROUND = '#E3F6E8'
const FREE_TEXT = '#1B6B35'

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.25),
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 12,
    fontSize: 13,
    lineHeight: 1.5,
    '& svg': {
      flexShrink: 0,
      marginTop: 1,
      fontSize: 18,
    },
  },
  free: {
    backgroundColor: FREE_BACKGROUND,
    color: FREE_TEXT,
  },
  policy: {
    backgroundColor: SOFT_PANEL,
    border: `1px solid ${CARD_BORDER}`,
    color: theme.palette.text.primary,
    '& svg': {
      color: theme.palette.secondary.main,
    },
  },
  title: {
    display: 'block',
    margin: 0,
    fontWeight: 700,
  },
  text: {
    margin: 0,
    whiteSpace: 'pre-line',
    overflowWrap: 'anywhere',
  },
}))

export const formatFreeCancellationWindow = hours =>
  `Free cancellation up to ${hours} ${
    hours === 1 ? 'hour' : 'hours'
  } before your lesson.`

// Student-facing cancellation callout for the private booking steps.
//
// The API exposes exactly two things today: `coach.canStudentCancel` and one
// free-text `coach.cancellationPolicy` (only returned while cancellations are
// allowed). There is no structured "free cancellation window", so the "Free
// cancellation up to N hours" promise is only shown when a caller passes
// `freeCancellationHours` (reserved for a future backend field). Otherwise the
// coach's own policy text is shown, and with no policy nothing is rendered so
// we never promise a refund the coach has not offered.
const CancellationCallout = ({
  canStudentCancel,
  cancellationPolicy,
  freeCancellationHours,
  className,
}) => {
  const classes = useStyles()

  const allowsCancel = toBoolean(canStudentCancel)
  const hours = Number(freeCancellationHours)
  if (allowsCancel && Number.isFinite(hours) && hours > 0) {
    return (
      <div
        className={clsx(classes.root, classes.free, className)}
        data-testid="cancellation-callout"
      >
        <ShieldIcon aria-hidden="true" />
        <p className={classes.text}>{formatFreeCancellationWindow(hours)}</p>
      </div>
    )
  }

  const policy = cancellationPolicy?.trim()
  if (!allowsCancel || !policy) return null

  return (
    <div
      className={clsx(classes.root, classes.policy, className)}
      data-testid="cancellation-callout"
    >
      <ShieldIcon aria-hidden="true" />
      <div>
        <span className={classes.title}>Cancellation policy</span>
        <p className={classes.text}>{policy}</p>
      </div>
    </div>
  )
}

CancellationCallout.propTypes = {
  canStudentCancel: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  cancellationPolicy: PropTypes.string,
  freeCancellationHours: PropTypes.number,
  className: PropTypes.string,
}

export default CancellationCallout
